import { Check } from "lucide-react";
import { useEffect } from "react";
import { create } from "zustand";
import { Dialog, DialogContent } from "@/components/ui/overlay";
import { Switch } from "@/components/ui/controls";
import { cn } from "@/lib/utils";
import type { PluginCommand } from "./registry";

/**
 * Plugin "Cor da barra lateral" (`kompil.sidebar-color`): pinta a barra de ferramentas (e, se a
 * pessoa quiser, o painel lateral) com uma cor misturada ao painel do tema — fica suave no escuro e
 * no claro, e os ícones continuam legíveis.
 */
export const SIDEBAR_COLORS = [
  { id: "azul", label: "Azul-noite", color: "#3b82f6" },
  { id: "roxo", label: "Roxo", color: "#8b5cf6" },
  { id: "verde", label: "Verde", color: "#10b981" },
  { id: "ciano", label: "Ciano", color: "#06b6d4" },
  { id: "ambar", label: "Âmbar", color: "#f59e0b" },
  { id: "vinho", label: "Vinho", color: "#e11d48" },
  { id: "rosa", label: "Rosa", color: "#ec4899" },
  { id: "grafite", label: "Grafite", color: "#a1a1aa" },
] as const;
export type SidebarColorId = (typeof SIDEBAR_COLORS)[number]["id"];

const KEY = "kompil.sidebarColor";

function read(): { color: SidebarColorId; panel: boolean } {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as { color?: string; panel?: boolean } | null;
    const color = SIDEBAR_COLORS.find((c) => c.id === raw?.color)?.id ?? "azul";
    return { color, panel: raw?.panel === true };
  } catch {
    return { color: "azul", panel: false };
  }
}

export const useSidebarColor = create<{
  color: SidebarColorId;
  /** Pinta também o painel lateral (Explorador, Buscar…). */
  panel: boolean;
  dialog: boolean;
  set(patch: Partial<{ color: SidebarColorId; panel: boolean; dialog: boolean }>): void;
}>((set, get) => ({
  ...read(),
  dialog: false,
  set: (patch) => {
    set(patch);
    const { color, panel } = get();
    try {
      localStorage.setItem(KEY, JSON.stringify({ color, panel }));
    } catch {
      // sem armazenamento: a cor vale só nesta sessão
    }
  },
}));

export const openSidebarColors = () => useSidebarColor.getState().set({ dialog: true });

export const SIDEBAR_COLOR_COMMANDS: PluginCommand[] = [
  { id: "sidebar-color.pick", title: "Cor da barra lateral: escolher…", run: openSidebarColors },
];

const VARS = ["--sidebar-bg", "--sidebar-active", "--sidebar-border", "--sidebar-accent"] as const;

/** Montado enquanto o plugin está ligado: aplica a cor e oferece o seletor. */
export function SidebarColorOverlay() {
  const { color, panel, dialog, set } = useSidebarColor();
  useEffect(() => {
    const root = document.documentElement;
    const c = SIDEBAR_COLORS.find((x) => x.id === color)!.color;
    root.style.setProperty("--sidebar-bg", `color-mix(in srgb, ${c} 16%, var(--panel))`);
    root.style.setProperty("--sidebar-active", `color-mix(in srgb, ${c} 38%, var(--panel))`);
    root.style.setProperty("--sidebar-border", `color-mix(in srgb, ${c} 34%, var(--panel-border))`);
    root.style.setProperty("--sidebar-accent", c);
    root.classList.toggle("sidebar-tint-panel", panel);
    return () => {
      for (const v of VARS) root.style.removeProperty(v);
      root.classList.remove("sidebar-tint-panel");
    };
  }, [color, panel]);
  return (
    <Dialog open={dialog} onOpenChange={(open) => set({ dialog: open })}>
      <DialogContent
        title="Cor da barra lateral"
        description="A cor se mistura ao tema: fica suave no escuro e no claro."
      >
        <div className="grid grid-cols-4 gap-2.5" role="radiogroup" aria-label="Cores">
          {SIDEBAR_COLORS.map((c) => {
            const on = c.id === color;
            return (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => set({ color: c.id })}
                className={cn(
                  "group flex flex-col items-center gap-2 rounded-xl border p-2.5 text-[12px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/60",
                  on
                    ? "border-fg/60 bg-bg-emphasis text-fg"
                    : "border-border text-fg-muted hover:bg-bg-muted",
                )}
              >
                <span
                  className="relative flex h-10 w-7 flex-col items-center gap-1 rounded-full border p-1"
                  style={{
                    background: `color-mix(in srgb, ${c.color} 16%, var(--panel))`,
                    borderColor: `color-mix(in srgb, ${c.color} 34%, var(--panel-border))`,
                  }}
                >
                  <span
                    className="h-3.5 w-3.5 rounded-full"
                    style={{ background: `color-mix(in srgb, ${c.color} 38%, var(--panel))` }}
                  />
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: c.color }} />
                  {on ? (
                    <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-fg text-fg-inverse">
                      <Check className="h-2.5 w-2.5" strokeWidth={3} />
                    </span>
                  ) : null}
                </span>
                {c.label}
              </button>
            );
          })}
        </div>
        <label className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5 text-[13px]">
          <span>
            Pintar também o painel lateral
            <span className="block text-[11.5px] text-fg-subtle">
              Explorador, Buscar, Git e os outros painéis
            </span>
          </span>
          <Switch
            checked={panel}
            onCheckedChange={(v) => set({ panel: v })}
            aria-label="Pintar o painel lateral"
          />
        </label>
      </DialogContent>
    </Dialog>
  );
}
